import GlobalStyles from "@mui/material/GlobalStyles";
import { useEffect } from "react";
// eslint-disable-next-line no-restricted-imports -- the only sanctioned sonner Toaster
import { Toaster, type ToasterProps } from "sonner";
import {
	AUTO_CLOSE_ANIMATION_VAR,
	AUTO_CLOSE_ANIMATIONS,
	AUTO_CLOSE_DURATION_VAR,
	AUTO_CLOSE_TOAST_CLASS,
	TOAST_DURATION_MS,
} from "../toast";
import { TOAST_COUNTDOWN_CLASS } from "./toast-view";

const countdownKeyframes = {
	from: { transform: "scaleX(1)" },
	to: { transform: "scaleX(0)" },
};

/** Set on <html> while the tab is hidden, when sonner also pauses its timers. */
const TIMERS_PAUSED_ATTR = "data-served-toast-timers-paused";

const autoCloseToast = `[data-sonner-toaster] [data-sonner-toast][data-swipe-out="false"].${AUTO_CLOSE_TOAST_CLASS}`;
const countdown = `.${TOAST_COUNTDOWN_CLASS}`;

/**
 * Toasts render MUI surfaces (`ToastView`), so sonner only positions, stacks and swipes them.
 * Auto-closing toasts show a bottom-edge bar that drains over the toast's duration. It pauses
 * exactly when sonner pauses the auto-close timer: pointer over the stack, pressing/swiping,
 * hidden tab. Under `prefers-reduced-motion` the bar stays static.
 */
const toasterStyles = {
	[`@keyframes ${AUTO_CLOSE_ANIMATIONS[0]}`]: countdownKeyframes,
	[`@keyframes ${AUTO_CLOSE_ANIMATIONS[1]}`]: countdownKeyframes,
	// Lower specificity than sonner's mobile rule, which makes toasts full width.
	"[data-sonner-toast]:where([data-styled='false'])": {
		width: "var(--width)",
	},
	[countdown]: {
		display: "none",
		position: "absolute",
		left: 0,
		right: 0,
		bottom: 0,
		height: 3,
		backgroundColor: "currentColor",
		opacity: 0.4,
		transformOrigin: "left",
	},
	[`${autoCloseToast} ${countdown}`]: {
		display: "block",
		animation: `var(${AUTO_CLOSE_ANIMATION_VAR}, ${AUTO_CLOSE_ANIMATIONS[0]}) var(${AUTO_CLOSE_DURATION_VAR}, ${TOAST_DURATION_MS}ms) linear forwards`,
	},
	[[
		autoCloseToast.replace(
			"[data-sonner-toaster]",
			"[data-sonner-toaster]:hover",
		),
		autoCloseToast.replace(
			"[data-sonner-toaster]",
			"[data-sonner-toaster]:active",
		),
		`html[${TIMERS_PAUSED_ATTR}] ${autoCloseToast}`,
	]
		.map((selector) => `${selector} ${countdown}`)
		.join(", ")]: {
		animationPlayState: "paused",
	},
	"@media (prefers-reduced-motion)": {
		[countdown]: { animation: "none !important" },
	},
};

const usePauseCountdownWhileHidden = () => {
	useEffect(() => {
		const root = document.documentElement;
		const sync = () => {
			root.toggleAttribute(TIMERS_PAUSED_ATTR, document.hidden);
			// Apply the paused state now; hidden tabs may defer style recalculation.
			void root.offsetHeight;
		};

		sync();
		document.addEventListener("visibilitychange", sync);
		return () => {
			document.removeEventListener("visibilitychange", sync);
			root.removeAttribute(TIMERS_PAUSED_ATTR);
		};
	}, []);
};

/**
 * The app-wide toast container. Mount once per app, inside the theme provider.
 * Persistence and close-button rules live in `~served/ui-comps/toast`.
 */
export const AppToaster = ({
	position,
	visibleToasts,
}: Pick<ToasterProps, "position" | "visibleToasts">) => {
	usePauseCountdownWhileHidden();

	return (
		<>
			<GlobalStyles styles={toasterStyles} />
			<Toaster
				expand
				duration={TOAST_DURATION_MS}
				visibleToasts={visibleToasts}
				position={position}
				gap={16}
			/>
		</>
	);
};
